import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Select, Input, Tooltip, Radio } from "antd";
import moment from "moment";
import { useMonthlyAttendance, useFilterOptions } from "hooks/analytics/analytics";
import { KpiRow, KpiTile, CoverageNote, EmptyState, fmt } from "components/analytics";
import { exportSheetsToXlsx } from "helpers/exportXlsx";
import "./style.css";

/**
 * Monthly attendance sheet -- employees down, days of the month across.
 *
 * The layout a timesheet is expected to arrive in, and the basis of the monthly
 * Excel download. Rest days and public holidays are marked distinctly from a
 * genuine absence, because counting Sundays as absence is what made the old
 * absentee numbers unusable.
 */

const { Option } = Select;

const LEGEND = [
  { key: "present", label: "Present", className: "ma-present" },
  { key: "absent", label: "Absent", className: "ma-absent" },
  { key: "rest", label: "Rest day", className: "ma-rest" },
  { key: "holiday", label: "Public holiday", className: "ma-holiday" },
];

/** Prefers the API's site-local flag; falls back to the browser's date. */
const isUpcoming = (day) =>
  day.upcoming ?? moment(day.date, "YYYY-MM-DD").isAfter(moment(), "day");

const MonthlyAttendance = () => {
  const [month, setMonth] = useState(moment());
  const [contractorIds, setContractorIds] = useState([]);
  const [search, setSearch] = useState("");
  const [cellMode, setCellMode] = useState("mark");

  const { mutate: load, data: res, isLoading } = useMonthlyAttendance();
  const { data: optionsRes } = useFilterOptions();
  const contractors = optionsRes?.data?.contractors || [];

  const runQuery = useCallback(
    (targetMonth, targetContractors) => {
      load({
        fromDate: targetMonth.clone().startOf("month").format("YYYY-MM-DD"),
        toDate: targetMonth.clone().endOf("month").format("YYYY-MM-DD"),
        contractorIds: targetContractors?.length ? targetContractors : undefined,
        employmentStatus: "ALL",
      });
    },
    [load]
  );

  useEffect(() => {
    runQuery(month, contractorIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = res?.data;
  const calendar = data?.calendar || [];

  /** Client-side name/code filter, so typing does not re-query the month. */
  const employees = useMemo(() => {
    const list = data?.employees || [];
    if (!search.trim()) return list;
    const needle = search.trim().toLowerCase();
    return list.filter(
      (e) =>
        (e.employeeName || "").toLowerCase().includes(needle) ||
        (e.employeeCode || "").toLowerCase().includes(needle) ||
        (e.contractor || "").toLowerCase().includes(needle)
    );
  }, [data, search]);

  const totals = useMemo(() => {
    const list = data?.employees || [];
    const manDays = list.reduce((sum, e) => sum + (e.daysPresent || 0), 0);
    const hours = list.reduce((sum, e) => sum + (e.measuredHours || 0), 0);
    return { people: list.length, manDays, hours: Math.round(hours * 10) / 10 };
  }, [data]);

  const cellFor = (employee, day) => {
    const record = employee.days?.[String(day.day)];
    if (record) {
      return {
        className: "ma-present",
        mark: "P",
        hours: record.hours != null ? record.hours.toFixed(1) : "",
        // No hours means no real turnstile checkout -- the day was either left
        // open or closed by policy, so there is nothing measured to show.
        notCheckedOut: record.hours == null,
        title: `${employee.employeeName} · ${moment(day.date).format("ddd DD MMM")}\nIn ${
          record.firstIn || "—"
        } · Out ${record.lastOut || "not recorded"}${
          record.hours ? `\n${record.hours} h measured` : "\nHours not measured"
        }`,
      };
    }
    if (day.publicHoliday) {
      return { className: "ma-holiday", mark: "H", hours: "", title: "Public holiday" };
    }
    if (day.restDay) {
      return { className: "ma-rest", mark: "R", hours: "", title: "Rest day" };
    }
    // An upcoming working day has no status yet -- showing it as absent made
    // the current month look far worse than it is. Rest days and holidays are
    // known in advance, so they keep their marks above.
    if (isUpcoming(day)) {
      return { className: "ma-upcoming", mark: "", hours: "", title: "Upcoming" };
    }
    return { className: "ma-absent", mark: "A", hours: "", title: "No attendance recorded" };
  };

  /** Two tabs: the grid as shown, plus a per-employee summary. */
  const downloadExcel = () => {
    if (!employees.length) return;

    const header = [
      "Card ID",
      "Employee Name",
      "Contractor",
      "Designation",
      ...calendar.map((d) => `${d.day} ${d.dayOfWeek}`),
      "Days Present",
      "Attendance %",
      "Measured Hours",
    ];
    const gridRows = [header];
    employees.forEach((e) => {
      gridRows.push([
        e.employeeCode || "",
        e.employeeName || "",
        e.contractor || "",
        e.designation || "",
        ...calendar.map((d) => cellFor(e, d).mark),
        e.daysPresent ?? 0,
        e.attendancePercent ?? "",
        e.measuredHours ?? "",
      ]);
    });

    const summaryRows = [
      ["Card ID", "Employee Name", "Contractor", "Designation",
       "Days Present", "Working Days", "Attendance %", "Measured Hours"],
    ];
    employees.forEach((e) =>
      summaryRows.push([
        e.employeeCode || "",
        e.employeeName || "",
        e.contractor || "",
        e.designation || "",
        e.daysPresent ?? 0,
        data?.workingDays ?? "",
        e.attendancePercent ?? "",
        e.measuredHours ?? "",
      ])
    );

    const legendRows = [
      ["Key", "Meaning"],
      ["P", "Present -- at least one turnstile read that day"],
      ["A", "Absent -- no attendance recorded on a working day"],
      ["R", "Rest day"],
      ["H", "Public holiday"],
      [],
      ["Note", "Measured hours count only records with a real checkout. Days closed by policy show as present with no hours."],
    ];

    exportSheetsToXlsx(
      [
        { name: month.format("MMM YYYY"), rows: gridRows },
        { name: "Summary", rows: summaryRows },
        { name: "Key", rows: legendRows },
      ],
      `Monthly_Attendance_${month.format("YYYY_MM")}`
    );
  };

  return (
    <div className="an-page">
      <div className="ma-controls">
        <div className="ma-title title">
          <h1>Monthly attendance</h1>
          <p>One row per employee, one column per day</p>
        </div>

        <div className="ma-field">
          <label>Month</label>
          <DatePicker
            picker="month"
            value={month}
            allowClear={false}
            format="MMMM YYYY"
            disabledDate={(current) => current && current > moment().endOf("month")}
            onChange={(value) => {
              const next = value || moment();
              setMonth(next);
              runQuery(next, contractorIds);
            }}
          />
        </div>
        <div className="ma-field">
          <label>Contractor</label>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="All contractors"
            value={contractorIds}
            onChange={setContractorIds}
            onBlur={() => runQuery(month, contractorIds)}
            filterOption={(input, option) =>
              String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {contractors.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="ma-field">
          <label>Find employee</label>
          <Input
            allowClear
            placeholder="Name, card ID or contractor"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="ma-field ma-field-tight">
          <label>Cells show</label>
          <Radio.Group value={cellMode} onChange={(e) => setCellMode(e.target.value)} optionType="button">
            <Radio.Button value="mark">Mark</Radio.Button>
            <Radio.Button value="hours">Hours</Radio.Button>
          </Radio.Group>
        </div>
        <div className="ma-actions">
          <Button onClick={() => runQuery(month, contractorIds)} loading={isLoading}>
            Apply
          </Button>
          <Tooltip title={employees.length ? "" : "Nothing to export for this month"}>
            <Button
              type="primary"
              className="csv"
              disabled={!employees.length}
              onClick={downloadExcel}
            >
              Download Excel
            </Button>
          </Tooltip>
        </div>
      </div>

      <KpiRow>
        <KpiTile label="Employees" tone="primary" value={fmt(totals.people)} foot={month.format("MMMM YYYY")} />
        <KpiTile label="Man-days" value={fmt(totals.manDays)} foot="Total days present" />
        <KpiTile
          label="Working days"
          value={fmt(data?.workingDays)}
          foot={
            month.isSame(moment(), "month")
              ? "To date, excluding rest days and holidays"
              : "Excluding rest days and holidays"
          }
        />
        <KpiTile label="Measured hours" value={fmt(totals.hours, 1)} foot="Real checkouts only" />
      </KpiRow>

      <CoverageNote coverage={data?.coverage} />

      <div className="ma-legend">
        {LEGEND.map((item) => (
          <span key={item.key} className="ma-legend-item">
            <span className={`ma-swatch ${item.className}`} />
            {item.label}
          </span>
        ))}
      </div>

      {employees.length ? (
        <div className="ma-grid-wrap">
          <table className="ma-grid">
            <thead>
              <tr>
                <th className="ma-sticky ma-col-emp">Employee</th>
                <th className="ma-col-contractor">Contractor</th>
                {calendar.map((day) => (
                  <th
                    key={day.date}
                    className={`ma-col-day ${day.restDay ? "is-rest" : ""} ${
                      day.publicHoliday ? "is-holiday" : ""
                    }`}
                    title={moment(day.date).format("dddd, DD MMM YYYY")}
                  >
                    <span className="ma-day-num">{day.day}</span>
                    <span className="ma-day-dow">{day.dayOfWeek}</span>
                  </th>
                ))}
                <th className="ma-col-total">Days</th>
                <th className="ma-col-total">%</th>
                <th className="ma-col-total">Hours</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.employeeId}>
                  <td className="ma-sticky ma-col-emp">
                    <span className="ma-emp-name">{employee.employeeName || "—"}</span>
                    <span className="ma-emp-code">{employee.employeeCode || ""}</span>
                  </td>
                  <td className="ma-col-contractor">{employee.contractor || "—"}</td>
                  {calendar.map((day) => {
                    const cell = cellFor(employee, day);
                    const showHours = cellMode === "hours";
                    return (
                      <td key={day.date} className={`ma-cell ${cell.className}`} title={cell.title}>
                        {showHours && cell.hours ? cell.hours : cell.mark}
                        {showHours && cell.notCheckedOut && (
                          <Tooltip title="Not checked out">
                            {/* Empty title stops the cell's own tooltip showing over this one. */}
                            <span className="ma-flag" title="">C</span>
                          </Tooltip>
                        )}
                      </td>
                    );
                  })}
                  <td className="ma-col-total ma-num">{fmt(employee.daysPresent)}</td>
                  <td className="ma-col-total ma-num">
                    {employee.attendancePercent === null || employee.attendancePercent === undefined
                      ? "—"
                      : `${employee.attendancePercent}%`}
                  </td>
                  <td className="ma-col-total ma-num">{fmt(employee.measuredHours, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="an-card">
          <EmptyState
            loading={isLoading}
            hasFilters={!!(contractorIds.length || search)}
            message={`No attendance recorded in ${month.format("MMMM YYYY")}`}
          />
        </div>
      )}
    </div>
  );
};

export default MonthlyAttendance;
